import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import {
  getIndustrialCostMaterialControl,
  getIndustrialCostMaterialStockProjection,
} from '../services/industrialCostService';
import type {
  MaterialControlResponse,
  MaterialControlRow,
  MaterialControlStatus,
  MaterialControlWarningSeverity,
  MaterialStockProjectionResponse,
  MaterialStockProjectionRow,
} from '../types/industrialCost';

import { esNumberFormat } from '../utils/numberFormat';
const CURRENT_YEAR = new Date().getFullYear();
const CURRENT_MONTH = new Date().getMonth() + 1;

const YEAR_OPTIONS = [2024, 2025, 2026, 2027, 2028];

const MONTH_OPTIONS = [
  { value: 1, label: 'Enero' },
  { value: 2, label: 'Febrero' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Mayo' },
  { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' },
  { value: 12, label: 'Diciembre' },
];

type StatusFilter = MaterialControlStatus | 'all';

function formatNumber(value: number | null | undefined, digits = 2) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';

  return esNumberFormat({
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  }).format(Number(value));
}

function formatDate(value: string | null | undefined) {
  if (!value) return '—';

  const date = new Date(`${value.slice(0, 10)}T00:00:00`);

  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('es-ES').format(date);
}

function formatMoney(value: number | null | undefined) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';

  return `${esNumberFormat({
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value))} €`;
}

function formatPercent(value: number | null | undefined) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';

  return `${esNumberFormat({
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(Number(value) * 100)} %`;
}

function formatUnit(value: string | null | undefined) {
  const text = String(value ?? '').trim();
  if (!text || ['nan', 'none', 'null', 'undefined'].includes(text.toLowerCase())) return '—';
  return text;
}

function formatPriceSource(source: string | null | undefined) {
  if (source === 'calculated_pmp') return 'Calculado PMP';
  if (source === 'manual') return 'Valor manual';
  if (source === 'latest_entry') return 'Última compra';
  if (!source) return 'Sin fuente';
  return source;
}

function buildPeriodStartDateLabel(year: number, month: number) {
  return `01/${String(month).padStart(2, '0')}/${year}`;
}

function buildPeriodClosingDateLabel(year: number, month: number) {
  const closingMonth = month === 12 ? 1 : month + 1;
  const closingYear = month === 12 ? year + 1 : year;

  return `01/${String(closingMonth).padStart(2, '0')}/${closingYear}`;
}

function materialTypeLabel(type: string | null | undefined) {
  if (!type) return 'Sin clasificación';

  switch (type) {
    case 'raw_material':
    case 'materia_prima':
      return 'Materia prima';
    case 'packaging':
    case 'envase':
      return 'Packaging';
    case 'ingredient':
      return 'Ingrediente';
    case 'consumable':
      return 'Consumible';
    default:
      return type;
  }
}

function statusChipSx(status: MaterialControlStatus) {
  switch (status) {
    case 'ok':
      return {
        color: '#bbf7d0',
        borderColor: 'rgba(34,197,94,0.45)',
        bgcolor: 'rgba(34,197,94,0.08)',
      };
    case 'warning':
      return {
        color: '#fde68a',
        borderColor: 'rgba(245,158,11,0.55)',
        bgcolor: 'rgba(245,158,11,0.08)',
      };
    case 'partial':
      return {
        color: '#bae6fd',
        borderColor: 'rgba(56,189,248,0.45)',
        bgcolor: 'rgba(56,189,248,0.08)',
      };
    case 'missing':
      return {
        color: '#fecaca',
        borderColor: 'rgba(248,113,113,0.55)',
        bgcolor: 'rgba(248,113,113,0.08)',
      };
    default:
      return {
        color: '#cbd5e1',
        borderColor: 'rgba(148,163,184,0.32)',
        bgcolor: 'rgba(148,163,184,0.06)',
      };
  }
}

function warningChipSx(severity: MaterialControlWarningSeverity) {
  switch (severity) {
    case 'critical':
      return {
        color: '#fecaca',
        borderColor: 'rgba(248,113,113,0.55)',
        bgcolor: 'rgba(248,113,113,0.08)',
      };
    case 'warning':
      return {
        color: '#fde68a',
        borderColor: 'rgba(245,158,11,0.55)',
        bgcolor: 'rgba(245,158,11,0.08)',
      };
    case 'info':
    default:
      return {
        color: '#bae6fd',
        borderColor: 'rgba(56,189,248,0.45)',
        bgcolor: 'rgba(56,189,248,0.08)',
      };
  }
}

function materialDisplayName(row: MaterialControlRow) {
  return row.material_name ?? row.material_code ?? row.material_id;
}

function materialSecondaryText(row: MaterialControlRow) {
  return `${row.material_code ?? 'Sin código'} · ${materialTypeLabel(row.material_type)} · ${formatUnit(row.unit_label)}`;
}

function hasRelevantDeviation(row: MaterialControlRow) {
  return row.deviation_pct !== null && Math.abs(Number(row.deviation_pct)) >= 0.05;
}

function sortRows(rows: MaterialControlRow[]) {
  return [...rows].sort((a, b) => {
    const priority = (row: MaterialControlRow) => {
      if (row.status === 'missing') return 0;
      if (row.status === 'partial') return 1;
      if (row.status === 'warning') return 2;
      return 3;
    };

    const priorityDiff = priority(a) - priority(b);
    if (priorityDiff !== 0) return priorityDiff;

    const aName = String(a.material_code ?? a.material_name ?? '').toLowerCase();
    const bName = String(b.material_code ?? b.material_name ?? '').toLowerCase();

    return aName.localeCompare(bName, 'es');
  });
}

function projectionMaterialDisplayName(row: MaterialStockProjectionRow) {
  return row.material_name ?? row.material_code ?? row.material_id;
}

function projectionMaterialSecondaryText(row: MaterialStockProjectionRow) {
  return `${row.material_code ?? 'Sin código'} · ${materialTypeLabel(row.material_type)} · ${formatUnit(row.unit_label)}`;
}

function sortProjectionRows(rows: MaterialStockProjectionRow[]) {
  return [...rows].sort((a, b) => {
    const priority = (row: MaterialStockProjectionRow) => {
      if (row.projected_balance_qty !== null && row.projected_balance_qty < 0) return 0;
      if (row.status === 'missing') return 1;
      if (row.status === 'partial') return 2;
      if (row.status === 'warning') return 3;
      return 4;
    };

    const priorityDiff = priority(a) - priority(b);

    if (priorityDiff !== 0) {
      return priorityDiff;
    }

    return String(a.material_code ?? a.material_name ?? '').localeCompare(
      String(b.material_code ?? b.material_name ?? ''),
      'es',
    );
  });
}

export default function IndustrialCostMaterialControlView() {
  const [selectedYear, setSelectedYear] = useState<number>(CURRENT_YEAR);
  const [selectedMonth, setSelectedMonth] = useState<number>(CURRENT_MONTH);

  const [projectionData, setProjectionData] =
    useState<MaterialStockProjectionResponse | null>(null);
  const [projectionLoading, setProjectionLoading] = useState<boolean>(true);
  const [projectionError, setProjectionError] = useState<string | null>(null);

  const [controlData, setControlData] = useState<MaterialControlResponse | null>(null);
  const [selectedMaterialId, setSelectedMaterialId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [loading, setLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const selectedMonthLabel = useMemo(
    () => MONTH_OPTIONS.find((option) => option.value === selectedMonth)?.label ?? 'Mes',
    [selectedMonth],
  );

  const openingDateLabel = useMemo(
    () => buildPeriodStartDateLabel(selectedYear, selectedMonth),
    [selectedYear, selectedMonth],
  );

  const closingDateLabel = useMemo(
    () => buildPeriodClosingDateLabel(selectedYear, selectedMonth),
    [selectedYear, selectedMonth],
  );

  useEffect(() => {
    let cancelled = false;

    async function loadMaterialStockProjection() {
      setProjectionLoading(true);
      setProjectionError(null);

      try {
        const response = await getIndustrialCostMaterialStockProjection();

        if (!cancelled) {
          setProjectionData(response);
        }
      } catch {
        if (!cancelled) {
          setProjectionData(null);
          setProjectionError(
            'No se pudo cargar el stock teórico y las necesidades pendientes.',
          );
        }
      } finally {
        if (!cancelled) {
          setProjectionLoading(false);
        }
      }
    }

    void loadMaterialStockProjection();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadMaterialControl() {
      setLoading(true);
      setErrorMessage(null);

      try {
        const response = await getIndustrialCostMaterialControl({
          period_year: selectedYear,
          period_month: selectedMonth,
        });

        if (cancelled) return;

        setControlData(response);

        setSelectedMaterialId((currentSelectedMaterialId) => {
          if (response.rows.length === 0) {
            return null;
          }

          if (
            currentSelectedMaterialId &&
            response.rows.some((row) => row.material_id === currentSelectedMaterialId)
          ) {
            return currentSelectedMaterialId;
          }

          return response.rows[0].material_id;
        });
      } catch {
        if (!cancelled) {
          setControlData(null);
          setSelectedMaterialId(null);
          setErrorMessage('No se pudo cargar el control de materiales del periodo.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void loadMaterialControl();

    return () => {
      cancelled = true;
    };
  }, [selectedYear, selectedMonth]);

  const rows = controlData?.rows ?? [];

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    return sortRows(rows).filter((row) => {
      const materialText = [
        row.material_code,
        row.material_name,
        row.material_type,
        row.unit_label,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const matchesSearch = normalizedSearch === '' || materialText.includes(normalizedSearch);
      const matchesStatus = statusFilter === 'all' || row.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [rows, searchText, statusFilter]);

  const selectedRow = useMemo(() => {
    if (!selectedMaterialId) return filteredRows[0] ?? rows[0] ?? null;

    return (
      rows.find((row) => row.material_id === selectedMaterialId) ??
      filteredRows[0] ??
      rows[0] ??
      null
    );
  }, [filteredRows, rows, selectedMaterialId]);

  const summary = controlData?.summary;

  const projectionRows = useMemo(
    () => sortProjectionRows(projectionData?.rows ?? []),
    [projectionData],
  );

  const projectionSummary = projectionData?.summary;

  return (
    <Stack spacing={3}>
      <Paper
        sx={{
          p: 0,
          overflow: 'hidden',
          borderRadius: 4,
          bgcolor: 'rgba(15,23,42,0.72)',
          border: '1px solid rgba(148,163,184,0.16)',
        }}
      >
        <Stack spacing={2.5} sx={{ p: 3 }}>
          <Box>
            <Typography
              variant="overline"
              sx={{
                color: '#38bdf8',
                fontWeight: 900,
                letterSpacing: 1.2,
              }}
            >
              Materiales · Situación actual
            </Typography>

            <Typography
              variant="h4"
              sx={{
                mt: 0.5,
                color: '#f8fafc',
                fontWeight: 950,
                letterSpacing: '-0.8px',
              }}
            >
              Stock y cobertura prevista
            </Typography>

            <Typography sx={{ mt: 1, color: '#94a3b8', maxWidth: 980 }}>
              Stock calculado = último recuento físico + entradas posteriores -
              consumo técnico de la producción posterior. El balance previsto descuenta
              además las necesidades de las órdenes de fabricación que siguen pendientes.
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                xl: 'repeat(4, minmax(0, 1fr))',
              },
              gap: 1.5,
            }}
          >
            {[
              {
                label: 'Materiales',
                value: projectionSummary?.materials_count ?? 0,
                helper: `${projectionSummary?.materials_with_baseline ?? 0} con recuento de partida`,
              },
              {
                label: 'Stock calculable',
                value: projectionSummary?.materials_with_calculated_stock ?? 0,
                helper: 'Con trazabilidad temporal suficiente',
              },
              {
                label: 'Déficits previstos',
                value: projectionSummary?.materials_with_projected_shortage ?? 0,
                helper: 'Balance previsto inferior a cero',
              },
              {
                label: 'OF pendientes',
                value: projectionSummary?.pending_orders_count ?? 0,
                helper: `${projectionSummary?.unresolved_pending_orders_count ?? 0} pendientes de resolver`,
              },
            ].map((item) => (
              <Paper
                key={item.label}
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  bgcolor: 'rgba(2,6,23,0.34)',
                  border: '1px solid rgba(148,163,184,0.12)',
                }}
              >
                <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 800 }}>
                  {item.label}
                </Typography>
                <Typography sx={{ color: '#f8fafc', fontWeight: 900, fontSize: '1.22rem' }}>
                  {item.value}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  {item.helper}
                </Typography>
              </Paper>
            ))}
          </Box>

          <Typography variant="caption" sx={{ color: '#64748b' }}>
            Último recuento: {formatDate(projectionSummary?.latest_inventory_count_date)}
            {' · '}
            Última entrada: {formatDate(projectionSummary?.latest_entry_date)}
            {' · '}
            Última producción: {formatDate(projectionSummary?.latest_production_date)}
          </Typography>

          {projectionData?.workload_by_machine?.length ? (
            <Stack spacing={1.5}>
              <Box>
                <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                  Carga de trabajo pendiente por máquina
                </Typography>
                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                  Metros pendientes de fabricar del último snapshot publicado.
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(2, minmax(0, 1fr))',
                    md: 'repeat(3, minmax(0, 1fr))',
                    xl: 'repeat(5, minmax(0, 1fr))',
                  },
                  gap: 1.5,
                }}
              >
                {projectionData.workload_by_machine.map((item) => (
                  <Paper
                    key={item.machine}
                    elevation={0}
                    sx={{
                      p: 1.75,
                      borderRadius: 3,
                      bgcolor: 'rgba(2,6,23,0.34)',
                      border: '1px solid rgba(148,163,184,0.16)',
                    }}
                  >
                    <Typography sx={{ color: '#38bdf8', fontWeight: 800 }}>
                      {item.machine}
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.5,
                        color: '#f8fafc',
                        fontSize: '1.3rem',
                        fontWeight: 900,
                      }}
                    >
                      {formatNumber(item.pending_m)} m
                    </Typography>

                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                      {item.orders_count} OF pendientes
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </Stack>
          ) : null}

          {projectionData && projectionData.unresolved_pending_orders.length > 0 ? (
            <Alert severity="warning" variant="outlined">
              Hay {projectionData.unresolved_pending_orders.length} órdenes pendientes que
              todavía no pueden convertirse de forma segura en necesidades de materia prima.
              {' '}
              {projectionData.unresolved_pending_orders
                .slice(0, 5)
                .map((order) => order.manufacturing_order_number)
                .join(', ')}
              {projectionData.unresolved_pending_orders.length > 5 ? '…' : ''}
            </Alert>
          ) : null}

          {projectionError ? (
            <Alert severity="error" variant="outlined">
              {projectionError}
            </Alert>
          ) : null}
        </Stack>

        <Divider sx={{ borderColor: 'rgba(148,163,184,0.12)' }} />

        {projectionLoading ? (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 5 }}>
            <CircularProgress size={28} />
            <Typography sx={{ color: '#94a3b8' }}>
              Calculando stock y necesidades...
            </Typography>
          </Stack>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 1120 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Material</TableCell>
                  <TableCell align="right">Recuento base</TableCell>
                  <TableCell align="right">Entradas</TableCell>
                  <TableCell align="right">Consumo técnico</TableCell>
                  <TableCell align="right">Stock calculado</TableCell>
                  <TableCell align="right">Necesidad pendiente</TableCell>
                  <TableCell align="right">Balance previsto</TableCell>
                  <TableCell>Estado</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {projectionRows.map((row) => (
                  <TableRow key={row.material_id} hover>
                    <TableCell>
                      <Stack spacing={0.25}>
                        <Typography sx={{ fontWeight: 900, color: '#f8fafc' }}>
                          {projectionMaterialDisplayName(row)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                          {projectionMaterialSecondaryText(row)}
                        </Typography>
                      </Stack>
                    </TableCell>

                    <TableCell align="right">
                      <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                        {formatNumber(row.baseline_count_qty)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        {formatDate(row.baseline_count_date)}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                        {formatNumber(row.entries_since_count_qty)}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                        {formatNumber(row.technical_consumption_since_count_qty)}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                        {formatNumber(row.calculated_stock_qty)}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                        {formatNumber(row.pending_requirement_qty)}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Typography
                        sx={{
                          fontWeight: 950,
                          color:
                            row.projected_balance_qty !== null &&
                            row.projected_balance_qty < 0
                              ? '#fca5a5'
                              : '#f8fafc',
                        }}
                      >
                        {formatNumber(row.projected_balance_qty)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={row.status_label}
                        sx={{
                          fontWeight: 800,
                          ...statusChipSx(row.status),
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}

                {projectionRows.length === 0 && !projectionError ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Alert severity="info" sx={{ my: 1 }}>
                        No hay materiales disponibles para calcular la proyección.
                      </Alert>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>

      <Paper
        sx={{
          p: 3,
          borderRadius: 4,
          bgcolor: 'rgba(15,23,42,0.72)',
          border: '1px solid rgba(148,163,184,0.16)',
        }}
      >
        <Stack spacing={2.5}>
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', lg: 'flex-start' }}
          >
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: '#38bdf8',
                  fontWeight: 900,
                  letterSpacing: 1.2,
                }}
              >
                Materiales · Stock y consumo
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  mt: 0.5,
                  color: '#f8fafc',
                  fontWeight: 950,
                  letterSpacing: '-0.8px',
                }}
              >
                Control de materiales
              </Typography>
              <Typography sx={{ mt: 1, color: '#94a3b8', maxWidth: 860 }}>
                Balance mensual de materiales. Para {selectedMonthLabel.toLowerCase()} {selectedYear},
                PROA compara apertura {openingDateLabel} + entradas del mes - cierre {closingDateLabel}
                contra el consumo técnico esperado por producción y receta.
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
              <TextField
                select
                label="Año"
                size="small"
                value={selectedYear}
                onChange={(event) => setSelectedYear(Number(event.target.value))}
                sx={{ minWidth: 120 }}
              >
                {YEAR_OPTIONS.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Mes"
                size="small"
                value={selectedMonth}
                onChange={(event) => setSelectedMonth(Number(event.target.value))}
                sx={{ minWidth: 150 }}
              >
                {MONTH_OPTIONS.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                xl: 'repeat(5, minmax(0, 1fr))',
              },
              gap: 1.5,
            }}
          >
            {[
              {
                label: 'Materiales analizados',
                value: summary?.materials_count ?? 0,
                helper: `${selectedMonthLabel} ${selectedYear}`,
              },
              {
                label: 'Valor stock cierre',
                value: formatMoney(summary?.closing_stock_value),
                helper: `Inventario ${closingDateLabel} valorado`,
              },
              {
                label: 'Consumo real calculado',
                value: formatMoney(summary?.real_consumption_value),
                helper: `${openingDateLabel} + entradas - ${closingDateLabel}`,
              },
              {
                label: 'Consumo técnico esperado',
                value: formatMoney(summary?.technical_consumption_value),
                helper: 'Producción × receta técnica',
              },
              {
                label: 'Desviación de consumo',
                value: formatMoney(summary?.deviation_value),
                helper: 'Consumo real - consumo técnico',
              },
            ].map((item) => (
              <Paper
                key={item.label}
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  bgcolor: 'rgba(2,6,23,0.34)',
                  border: '1px solid rgba(148,163,184,0.12)',
                }}
              >
                <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 800 }}>
                  {item.label}
                </Typography>
                <Typography sx={{ color: '#f8fafc', fontWeight: 900, fontSize: '1.22rem' }}>
                  {item.value}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  {item.helper}
                </Typography>
              </Paper>
            ))}
          </Box>

          {summary && summary.materials_with_warnings > 0 ? (
            <Alert severity="info" variant="outlined">
              Hay {summary.materials_with_warnings} materiales con avisos. Revisa especialmente
              desviaciones relevantes, materiales sin cierre y materiales sin valoración económica.
            </Alert>
          ) : null}

          {errorMessage ? (
            <Alert severity="error" variant="outlined">
              {errorMessage}
            </Alert>
          ) : null}
        </Stack>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.7fr) minmax(360px, 0.8fr)' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        <Paper
          sx={{
            p: 0,
            overflow: 'hidden',
            borderRadius: 4,
            bgcolor: 'rgba(15,23,42,0.72)',
            border: '1px solid rgba(148,163,184,0.16)',
          }}
        >
          <Box
            sx={{
              px: 3,
              py: 2.25,
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              gap: 1.5,
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', lg: 'center' },
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 900, fontSize: '1rem', color: '#f8fafc' }}>
                Balance mensual por material
              </Typography>
              <Typography sx={{ color: '#94a3b8', mt: 0.5 }}>
                Una fila por material relevante del periodo. El consumo real se calcula como
                apertura + entradas - cierre; el consumo técnico viene de producción × receta.
                El proveedor se muestra como desglose de stock y entradas, no como consumo imputado.
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
              <TextField
                label="Buscar"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                size="small"
                sx={{ minWidth: { xs: '100%', sm: 260 } }}
              />

              <TextField
                select
                label="Estado"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
                size="small"
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="ok">Correctos</MenuItem>
                <MenuItem value="warning">Con avisos</MenuItem>
                <MenuItem value="partial">Parciales</MenuItem>
                <MenuItem value="missing">Sin valoración</MenuItem>
              </TextField>
            </Stack>
          </Box>

          <Divider sx={{ borderColor: 'rgba(148,163,184,0.12)' }} />

          {loading ? (
            <Stack alignItems="center" spacing={1.5} sx={{ py: 5 }}>
              <CircularProgress size={28} />
              <Typography sx={{ color: '#94a3b8' }}>
                Cargando control de materiales...
              </Typography>
            </Stack>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Material</TableCell>
                  <TableCell align="right">Consumo real</TableCell>
                  <TableCell align="right">Consumo técnico</TableCell>
                  <TableCell align="right">Desviación</TableCell>
                  <TableCell align="right">Precio</TableCell>
                  <TableCell align="right">
                    Stock cierre
                    <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8' }}>
                      {closingDateLabel}
                    </Typography>
                  </TableCell>
                  <TableCell>Estado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map((row) => {
                  const selected = selectedRow?.material_id === row.material_id;

                  return (
                    <TableRow
                      key={row.material_id}
                      hover
                      selected={selected}
                      onClick={() => setSelectedMaterialId(row.material_id)}
                      sx={{
                        cursor: 'pointer',
                        '&.Mui-selected': {
                          bgcolor: 'rgba(56,189,248,0.08)',
                        },
                        '&.Mui-selected:hover': {
                          bgcolor: 'rgba(56,189,248,0.11)',
                        },
                      }}
                    >
                      <TableCell>
                        <Stack spacing={0.25}>
                          <Typography sx={{ fontWeight: 900, color: '#f8fafc' }}>
                            {materialDisplayName(row)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                            {materialSecondaryText(row)}
                          </Typography>
                        </Stack>
                      </TableCell>

                      <TableCell align="right">
                        <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                          {formatNumber(row.real_consumption_qty)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                          {formatMoney(row.real_consumption_value)}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                          {formatNumber(row.technical_consumption_qty)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                          {formatMoney(row.technical_consumption_value)}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Typography
                          sx={{
                            fontWeight: 900,
                            color: hasRelevantDeviation(row) ? '#fde68a' : '#f8fafc',
                          }}
                        >
                          {formatNumber(row.deviation_qty)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                          {formatPercent(row.deviation_pct)}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                          {formatMoney(row.effective_unit_price)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                          {formatPriceSource(row.effective_valuation_source)}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                          {formatNumber(row.closing_qty)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                          {formatMoney(row.closing_stock_value)}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={row.status_label}
                          sx={{
                            fontWeight: 800,
                            ...statusChipSx(row.status),
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}

                {filteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Alert severity="info" sx={{ my: 1 }}>
                        No hay materiales para el periodo o los filtros actuales.
                      </Alert>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          )}
        </Paper>

        <Paper
          sx={{
            p: 0,
            overflow: 'hidden',
            borderRadius: 4,
            bgcolor: 'rgba(15,23,42,0.72)',
            border: '1px solid rgba(148,163,184,0.16)',
            position: { xl: 'sticky' },
            top: { xl: 24 },
          }}
        >
          <Box sx={{ px: 3, py: 2.25 }}>
            <Typography sx={{ fontWeight: 900, fontSize: '1rem', color: '#f8fafc' }}>
              Detalle del material
            </Typography>
            <Typography sx={{ color: '#94a3b8', mt: 0.5 }}>
              Balance físico, valoración, consumo técnico y desglose por proveedor.
            </Typography>
          </Box>

          <Divider sx={{ borderColor: 'rgba(148,163,184,0.12)' }} />

          {selectedRow ? (
            <Stack spacing={2.25} sx={{ p: 3 }}>
              <Box>
                <Typography sx={{ color: '#f8fafc', fontWeight: 950, fontSize: '1.15rem' }}>
                  {materialDisplayName(selectedRow)}
                </Typography>
                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                  {materialSecondaryText(selectedRow)}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: 1,
                }}
              >
                {[
                  { label: `Apertura ${openingDateLabel}`, value: formatNumber(selectedRow.opening_qty) },
                  { label: `Entradas ${selectedMonthLabel}`, value: formatNumber(selectedRow.entries_qty) },
                  { label: 'Disponible', value: formatNumber(selectedRow.available_qty) },
                  { label: `Cierre ${closingDateLabel}`, value: formatNumber(selectedRow.closing_qty) },
                  { label: 'Consumo real calculado', value: formatNumber(selectedRow.real_consumption_qty) },
                  { label: 'Consumo técnico esperado', value: formatNumber(selectedRow.technical_consumption_qty) },
                ].map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      p: 1.25,
                      borderRadius: 2.5,
                      bgcolor: 'rgba(2,6,23,0.28)',
                      border: '1px solid rgba(148,163,184,0.1)',
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 800 }}>
                      {item.label}
                    </Typography>
                    <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  bgcolor: alpha('#38bdf8', 0.06),
                  border: `1px solid ${alpha('#38bdf8', 0.16)}`,
                }}
              >
                <Typography variant="caption" sx={{ color: '#bae6fd', fontWeight: 900 }}>
                  Desviación
                </Typography>
                <Typography sx={{ color: '#f8fafc', fontWeight: 950, fontSize: '1.2rem' }}>
                  {formatNumber(selectedRow.deviation_qty)} · {formatMoney(selectedRow.deviation_value)}
                </Typography>
                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                  {formatPercent(selectedRow.deviation_pct)} respecto al consumo técnico esperado.
                </Typography>
              </Box>

              <Box>
                <Typography sx={{ color: '#f8fafc', fontWeight: 900, mb: 1 }}>
                  Valoración
                </Typography>
                <Stack spacing={0.75}>
                  <Typography sx={{ color: '#cbd5e1' }}>
                    Precio usado: <strong>{formatMoney(selectedRow.effective_unit_price)}</strong>
                  </Typography>
                  <Typography sx={{ color: '#94a3b8' }}>
                    Fuente: {formatPriceSource(selectedRow.effective_valuation_source)}
                  </Typography>
                  <Typography sx={{ color: '#94a3b8' }}>
                    Valor stock cierre: {formatMoney(selectedRow.closing_stock_value)}
                  </Typography>
                </Stack>
              </Box>

              <Divider sx={{ borderColor: 'rgba(148,163,184,0.12)' }} />

              <Box>
                <Typography sx={{ color: '#f8fafc', fontWeight: 900, mb: 1 }}>
                  Consumo técnico por producto
                </Typography>

                {selectedRow.technical_consumption_breakdown.length > 0 ? (
                  <Stack spacing={1}>
                    {selectedRow.technical_consumption_breakdown.map((item) => (
                      <Box
                        key={`${item.product_id}-${item.consumption_basis}`}
                        sx={{
                          p: 1.25,
                          borderRadius: 2.5,
                          bgcolor: 'rgba(2,6,23,0.28)',
                          border: '1px solid rgba(148,163,184,0.1)',
                        }}
                      >
                        <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                          {item.product_code ?? item.product_name ?? item.product_id}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                          Producción {formatNumber(item.production_qty)} {formatUnit(item.production_unit_label)}
                          {' · '}
                          Regla {formatNumber(item.technical_qty_per_unit, 6)}
                          {' · '}
                          Consumo {formatNumber(item.technical_consumption_qty)}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    No hay consumo técnico asociado a producción en este periodo.
                  </Typography>
                )}
              </Box>

              <Box>
                <Typography sx={{ color: '#f8fafc', fontWeight: 900, mb: 1 }}>
                  Proveedores
                </Typography>

                {selectedRow.supplier_breakdown.length > 0 ? (
                  <Stack spacing={1}>
                    {selectedRow.supplier_breakdown.map((item) => (
                      <Box
                        key={item.supplier_label}
                        sx={{
                          p: 1.25,
                          borderRadius: 2.5,
                          bgcolor: 'rgba(2,6,23,0.28)',
                          border: '1px solid rgba(148,163,184,0.1)',
                        }}
                      >
                        <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                          {item.supplier_label}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8' }}>
                          Apertura {formatNumber(item.opening_qty)} · Entradas {formatNumber(item.entries_qty)} · Cierre {formatNumber(item.closing_qty)}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8' }}>
                          Valor entradas {formatMoney(item.entries_value)} · Precio medio entrada {formatMoney(item.average_entry_unit_price)}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    No hay desglose por proveedor para este material.
                  </Typography>
                )}
              </Box>

              <Box>
                <Typography sx={{ color: '#f8fafc', fontWeight: 900, mb: 1 }}>
                  Avisos
                </Typography>

                {selectedRow.warnings.length > 0 ? (
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {selectedRow.warnings.map((warning) => (
                      <Chip
                        key={`${warning.code}-${warning.title}`}
                        size="small"
                        variant="outlined"
                        label={warning.title}
                        sx={{
                          fontWeight: 800,
                          ...warningChipSx(warning.severity),
                        }}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Sin avisos relevantes.
                  </Typography>
                )}
              </Box>
            </Stack>
          ) : (
            <Alert severity="info" sx={{ m: 3 }}>
              Selecciona un material para ver el detalle.
            </Alert>
          )}
        </Paper>
      </Box>
    </Stack>
  );
}